<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching && !initialized")
    v-skeleton-loader.py-8.px-4(type="list-item-three-line@5")
  template(v-else)
    //- Title and actions bars
    .text-md.font-semibold.mt-8.mb-4 {{ $t('files') }}
    v-row(dense)
      //- Data listing
      v-col(cols="7")
        .data-table
          //- Content
          v-card.round-lg.pa-4(outlined)
            //- Top Tabs
            v-row(no-gutters)
              v-col(cols="8")
                v-tabs(color="basic700" :height="44" show-arrows active-class="font-semibold")
                  v-tab.text-sm.font-medium.text-none.px-4 {{ $t('all') }}
              v-col(align="end")
                .d-flex.align-center
                  v-spacer
                  v-btn(@click="clickSearch" icon color="neutral500")
                    v-icon(size="20") $yzi-search-sm
                  v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
            v-divider
            //- Data
            .data-table
              v-data-table.mt-sm-4.pb-6(v-model="previewItem" :headers="headers" :items="patientAttachments" item-key="attachment.id" :items-per-page="pageSize" :page="currentPage" :loading="isFetching" hide-default-footer disable-sort :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2" single-select @click:row="onSelectPreviewItem")
                //- name
                template(v-slot:item.name="{ item }")
                  .text-sm.neutral700--text {{ item.attachment.name }}
                //- actions
                template(v-slot:item.actions="{ item }")
                  .d-flex.justify-end
                    v-tooltip(top color="black" content-class="black")
                      template(#activator="{ on, attrs }")
                        v-btn.mr-1(v-bind="attrs" v-on="on" icon small @click="onDownload(item)")
                          v-icon(size=20) $yzi-download-01
                      .text-sm.font-semibold.basic500--text {{ $t('download') }}
                    v-menu(offset-y left rounded="lg")
                      template(v-slot:activator="{ on }")
                        v-btn(icon small v-on="on")
                          v-icon mdi-dots-horizontal
                      v-list.py-0
                        v-list-item(@click="onEditItem(item)")
                          v-list-item-title.text-sm {{ $t('edit') }}
                        v-divider
                        v-list-item(@click="onDeleteItem(item)")
                          v-list-item-title.text-sm.danger500--text {{ $t('delete') }}
              div.d-flex.align-center.mb-4(v-if="totalCount > pageSize")
                v-spacer
                div
                  yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top)
      //- File preview
      v-col(cols="5")
        v-card.round-lg.pa-8.preview(outlined)
          template(v-if="previewItem.length")
            //- File details
            .text-sm.font-semibold.mb-4 {{ $t('file_details') }}
            //- Preview Image
            .preview-file.mb-4(v-if="previewItemDetails.type === 'image' || previewItemDetails.format === 'PDF'")
              .text-xs.font-semibold File preview
              div.mt-1
                v-card.py-0(v-if="previewItemDetails.type === 'image'" link @click="viewImage(previewItemDetails.file)" height="100" style="width: fit-content;")
                  img.mb-n2(:src="previewItemDetails.file" loading="eager" style="height: inherit;")
                y-button(v-else-if="previewItemDetails.format === 'PDF'" label="View file" @click="viewPDF(previewItemDetails.file)" size="sm" outlined prependIcon="mdi-file-pdf-box" iconSize="25" color="danger500" button-class="pl-2 pr-3")
            //- File format
            .file-format.mb-4
              .text-xs.font-semibold File format
              .text-xs {{ previewItemDetails.format }}
            //- File size
            .file-size.mb-4
              .text-xs.font-semibold {{ $t('size') }}
              .text-xs {{ previewItemDetails.size }}
            //- Created
            .created.mb-4
              .text-xs.font-semibold {{ $t('created') }}
              .text-xs {{ previewItemDetails.created }}
            //- Uploaded by
            .uploaded-by.mb-4
              .text-xs.font-semibold {{ $t('uploaded_by') }}
              y-chip(v-if="previewItemDetails.uploader" size="sm" chip-class="px-2" outlined)
                intel-avatar.mr-1.mt-1(:code="previewItemDetails.uploader.avatar_code" :id="previewItemDetails.uploader.id" size=18)
                .text-xs {{ previewItemDetails.uploader.fullName }}
          template(v-else)
            .d-flex.align-center.justify-center.fill-height
              .text-sm.neutral500--text No preview data
  .dialogs
    edit-attachment-dialog(v-if="showEditDialog" :open.sync="showEditDialog" :pid="selectedItem.lead_pid" :file="selectedItem.attachment" @onUpdated="fetchAttachments")
    upload-attachment-dialog(v-if="showUploadDialog" :open.sync="showUploadDialog" :pid="selectedItem.lead_pid" @onUploaded="fetchAttachments")
    delete-attachment-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :pid="selectedItem.lead_pid" :file="selectedItem.attachment" @deleted="fetchAttachments")
    view-image-dialog(v-if="showViewImageDialog" :open.sync="showViewImageDialog" :image="selectedImage" @closed="selectedImage = null")

</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import DeleteAttachmentDialog from '@/components/clinic/dialogs/DeleteAttachmentDialog.vue'
import EditAttachmentDialog from '@/components/clinic/dialogs/EditAttachmentDialog.vue'
import UploadAttachmentDialog from '@/components/clinic/dialogs/UploadAttachmentDialog.vue'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'
import IntelAvatar from '@/components/IntelAvatar.vue'

export default {
  components: {
    UploadAttachmentDialog, EditAttachmentDialog, DeleteAttachmentDialog, ViewImageDialog, IntelAvatar
  },
  data() {
    return {
      isFetching: false,
      showSearchField: false,
      showViewImageDialog: false,
      showUploadDialog: false,
      showDeleteDialog: false,
      showEditDialog: false,
      initialized: false,
      searchText: null,
      selectedImage: null,
      selectedItem: null,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      previewItem: [],
      headers: [
        {
          text: 'Name',
          value: 'name'
        },
        {
          text: 'Upload Date',
          value: 'formatted_date'
        },
        {
          text: '',
          value: 'actions'
        }
      ],
      patientAttachments: [],
    }
  },
  watch: {
    currentPage() {
      this.fetchAttachments()
    },
    pageSize() {
      this.fetchAttachments()
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    }),
    patientId () {
      if (this.$route.params.id) return parseInt(this.$route.params.id)

      return null
    },
    params () {
      return {
        search: this.searchText,
        page: this.currentPage,
        page_size: this.pageSize
      }
    },
    previewItemDetails () {
      if (this.previewItem.length) {
        const item = this.previewItem[0]
        const data = {
          type: this.getFileType(item),
          size: this.$options.filters.bytesToSize(item.attachment.file_size),
          created: this.displayDate(item.attachment.created),
          uploader: this.getUploaderData(item),
          file: item.attachment.document.file,
          format: this.getFileFormat(item)
        }
        return data
      }
      return {}
    }
  },
  mounted () {
    this.fetchAttachments()
  },
  methods: {
    ...mapActions(['showMessage']),
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchAttachments()
    }, 750),
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    async fetchAttachments () {
      this.isFetching = true
      try {
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}/attachments`, { params: this.params })
        this.patientAttachments = data.results.map(result => this.sanitizeData(result))
        this.totalCount = data.count
        this.initialized = true
        this.previewItem = []
      // eslint-disable-next-line no-empty
      } catch (error) {
      } finally {
        this.isFetching = false
      }
    },
    sanitizeData (data) {
      // Record date
      const formatted_date = data.record_date ? this.displayDate(data.record_date) : null

      return {
        ...data,
        formatted_date
      }
    },
    displayDate (value) {
      if (!value) return null

      const date = DateTime.fromISO(value)
      const formatDate = date.toFormat('dd MMM yyyy')
      const formatTime = date.toFormat('HH:mm a').toLowerCase()

      return `${formatDate}, ${formatTime}`
    },
    onDownload (item) {
      window.open(item.attachment.document.file, '_blank')
    },
    onDeleteItem (item) {
      this.selectedItem = item
      this.showDeleteDialog = true
    },
    onEditItem (item) {
      this.selectedItem = item
      this.showEditDialog = true
    },
    onSelectPreviewItem (item) {
      this.previewItem = [item]
    },
    getFileType (item) {
      const type = item.attachment?.document?.type || null
      if (type) {
        const fileType = type.split('/').length > 0 ? type.split('/')[0].toLowerCase() : ''
        return fileType
      }
      return ''
    },
    getFileFormat (item) {
      const format = item.attachment?.document?.type || null
      if (format) {
        const fileFormat = format.split('/').length > 0 ? format.split('/')[1].toUpperCase() : ''
        return fileFormat
      }
      return ''
    },
    getUploaderData (item) {
      const uploader = item.attachment?.uploaded_by || null
      if (uploader) {
        const fullName = `${uploader.first_name} ${uploader.last_name}`
        return {
          ...uploader,
          fullName,
        }
      }
      return {}
    },
    viewImage (image) {
      this.selectedImage = image
      this.showViewImageDialog = true
    },
    viewPDF (file) {
      const url = `https://docs.google.com/viewer?url=${file}`
      window.open(url,'_blank')
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
.preview {
  height: 100%;
}
</style>